import { Alert, AlertTitle, Box } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useSelector } from 'react-redux'
import { useGetTimesheetQuery } from '../../utils/hooks/useGetTimesheetQuery'
import Loader from '../general/Loader'
import React, { useMemo } from 'react'
import { TimesheetStoreState } from '../../store/timesheetStore'
import dayjs from 'dayjs'
import { TimesheetStatusEnum } from '../../store/slices/timesheet/timesheetDetailSlice'

function SubHeaderDetails(): React.ReactElement {
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const pdfState = useSelector((state: TimesheetStoreState) => state.pdfSlice)

	const { isLoading } = useGetTimesheetQuery()

	const isPrimaryDriverNotApproved = useMemo(() => {
		return timesheetPage.services?.some(
			service =>
				service.service?.seconddriver &&
				service.mainSecundaryDriver !== null &&
				service.mainSecundaryDriverTimeSheetStatus !== 'Approved',
		)
	}, [timesheetPage])

	if (isLoading) {
		return <Loader />
	}

	if (pdfState.clicked) return <></>

	return (
		<Box
			sx={{
				marginRight: 1.5,
				display: 'grid',
				gap: 1,
			}}
		>
			{timesheetPage.status === TimesheetStatusEnum.Approved && (
				<Alert
					severity='success'
					sx={{
						width: '100%',
						height: '37px',
						padding: '0 16px',
						background: 'linear-gradient(86deg, #16C53D -57.78%, #00A5FF 115.35%)',
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 500,
						lettrerSpacing: '0.15px',
						lineHeight: '150%',
						color: '#FFFFFF',
						'& .MuiAlert-icon': {
							color: '#FFFFFF',
							fontWeight: 500,
						},
					}}
					icon={<CheckCircleOutlineIcon fontSize='inherit' />}
				>
					APROVADA: esta Folha de Ponto foi aprovada
				</Alert>
			)}
			{(timesheetPage.status === TimesheetStatusEnum.AutoSubmitted ||
				timesheetPage.status === TimesheetStatusEnum.SubmittedWithoutChanges ||
				timesheetPage.status === TimesheetStatusEnum.Submitted) && (
				<Alert
					severity='info'
					sx={{
						width: '100%',
						height: '37px',
						padding: '0 16px',
						background: 'linear-gradient(90deg, #00A5FF 0%, #37CDFF 100%)',
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 500,
						lettrerSpacing: '0.15px',
						lineHeight: '150%',
						color: '#FFFFFF',
						'& .MuiAlert-icon': {
							color: '#FFFFFF',
							fontWeight: 500,
						},
					}}
				>
					Esta Folha de Ponto foi submetida pelo motorista
				</Alert>
			)}
			{timesheetPage.status === TimesheetStatusEnum.Rejected && (
				<Alert
					severity='error'
					sx={{
						width: '100%',
						height: '37px',
						padding: '0 16px',
						background: 'linear-gradient(90deg, #D32F2F 0%, #FB736D 100%)',
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 500,
						lettrerSpacing: '0.15px',
						lineHeight: '150%',
						color: '#FFFFFF',
						'& .MuiAlert-icon': {
							color: '#FFFFFF',
							fontWeight: 500,
						},
					}}
				>
					<AlertTitle>RECUSADA: esta Folha de Ponto foi recusada</AlertTitle>
					Por favor, aguarde o novo envio da Folha de Ponto pelo Motorista e volte a rever.
				</Alert>
			)}
			{timesheetPage.status === TimesheetStatusEnum.NotSubmitted && (
				<Alert
					severity='warning'
					sx={{
						width: '100%',
						height: '37px',
						padding: '0 16px',
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 500,
						lettrerSpacing: '0.15px',
						lineHeight: '150%',
						color: '#FFFFFF',
						background: 'linear-gradient(90deg, #FF5C00 0.42%, #EDBF67 99.58%)',
						'& .MuiAlert-icon': {
							color: '#FFFFFF',
							fontWeight: 500,
						},
					}}
				>
					Esta Folha de Ponto não foi submetida pelo motorista
				</Alert>
			)}
			{isPrimaryDriverNotApproved && (
				<Alert
					severity='warning'
					sx={{
						width: '100%',
						height: '37px',
						padding: '0 16px',
						background: 'linear-gradient(90deg, #FF5C00 0.42%, #EDBF67 99.58%)',
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 500,
						lettrerSpacing: '0.15px',
						lineHeight: '150%',
						color: '#FFFFFF',
						'& .MuiAlert-icon': {
							color: '#FFFFFF',
							fontWeight: 500,
						},
					}}
				>
					Esta Folha de Ponto é do motorista secundário, mas ainda não foi aprovada pelo motorista principal. A Folha de
					Ponto só pode ser aprovada depois da aprovação da do motorista principal.
				</Alert>
			)}
			{timesheetPage.editedBy !== null && timesheetPage.editedDate !== null && (
				<Alert
					severity='warning'
					sx={{
						width: '100%',
						height: '37px',
						padding: '0 16px',
						background: 'linear-gradient(90deg, #00A5FF 0%, #37CDFF 100%)',
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 500,
						lettrerSpacing: '0.15px',
						lineHeight: '150%',
						color: '#FFFFFF',
						'& .MuiAlert-icon': {
							color: '#FFFFFF',
							fontWeight: 500,
						},
					}}
				>
					Folha de Ponto editada pelo escalador {timesheetPage.editedBy} -{' '}
					{dayjs(timesheetPage.editedDate).format('DD/MM/YYYY - HH:mm')}
				</Alert>
			)}
			{timesheetPage.status === TimesheetStatusEnum.AutoSubmitted && (
				<Alert
					severity='info'
					sx={{
						width: '100%',
						height: '37px',
						padding: '0 16px',
						background: 'linear-gradient(90deg, #00A5FF 0%, #37CDFF 100%)',
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 500,
						lettrerSpacing: '0.15px',
						lineHeight: '150%',
						color: '#FFFFFF',
						'& .MuiAlert-icon': {
							color: '#FFFFFF',
							fontWeight: 500,
						},
					}}
				>
					Esta Folha de Ponto foi submetida automaticamente pelo sistema.
				</Alert>
			)}
		</Box>
	)
}

export default SubHeaderDetails
